import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface Notice {
  id: string;
  title: string;
  content: string;
  date: string;
  views: number;
  createdAt?: string;
  updatedAt?: string;
}

interface NoticeContextType {
  notices: Notice[];
  addNotice: (notice: Omit<Notice, 'id' | 'views' | 'createdAt' | 'updatedAt'>) => void;
  updateNotice: (id: string, notice: Partial<Notice>) => void;
  deleteNotice: (id: string) => void;
  getNotice: (id: string) => Notice | undefined;
}

const NoticeContext = createContext<NoticeContextType | undefined>(undefined);

export const NoticeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notices, setNotices] = useState<Notice[]>(() => {
    // localStorage에서 데이터 불러오기
    const savedNotices = localStorage.getItem('notices');
    if (savedNotices) {
      return JSON.parse(savedNotices);
    }

    // 초기 샘플 데이터
    return [
      {
        id: '1',
        title: '2025년 상반기 교육 프로그램 신청 안내',
        date: '2025-01-15',
        content: '2025년 상반기 교육 프로그램 수강생을 모집합니다. 비즈니스 영어회화, AI/SW 전문교육 등 다양한 프로그램이 준비되어 있습니다.',
        views: 245,
        createdAt: '2025-01-15T09:00:00',
        updatedAt: '2025-01-15T09:00:00'
      },
      {
        id: '2',
        title: '설 연휴 센터 휴무 안내',
        date: '2025-01-20',
        content: '설 연휴 기간(1/28~1/30) 동안 센터는 휴무합니다. 긴급 문의사항은 이메일로 연락 부탁드립니다.',
        views: 189,
        createdAt: '2025-01-20T10:00:00',
        updatedAt: '2025-01-20T10:00:00'
      },
      {
        id: '3',
        title: 'AI/SW 전문교육 과정 개설 안내',
        date: '2025-01-10',
        content: '최신 AI 기술 교육 과정이 새롭게 개설되었습니다. Python 기초부터 머신러닝, 딥러닝까지 체계적으로 학습할 수 있습니다.',
        views: 312,
        createdAt: '2025-01-10T14:00:00',
        updatedAt: '2025-01-10T14:00:00'
      },
      {
        id: '4',
        title: '글로벌인재센터 시설 이용 안내',
        date: '2025-01-05',
        content: '센터 시설 이용 시간은 평일 09:00~18:00이며, 공간 예약은 홈페이지를 통해 가능합니다.',
        views: 156,
        createdAt: '2025-01-05T11:00:00',
        updatedAt: '2025-01-05T11:00:00'
      }
    ];
  });

  // 데이터 변경 시 localStorage에 저장
  useEffect(() => {
    localStorage.setItem('notices', JSON.stringify(notices));
  }, [notices]);

  const addNotice = (noticeData: Omit<Notice, 'id' | 'views' | 'createdAt' | 'updatedAt'>) => {
    const newNotice: Notice = {
      ...noticeData,
      id: Date.now().toString(),
      views: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setNotices(prev => [newNotice, ...prev]);
  };

  const updateNotice = (id: string, noticeData: Partial<Notice>) => {
    setNotices(prev => prev.map(notice =>
      notice.id === id
        ? { ...notice, ...noticeData, updatedAt: new Date().toISOString() }
        : notice
    ));
  };

  const deleteNotice = (id: string) => {
    setNotices(prev => prev.filter(notice => notice.id !== id));
  };

  const getNotice = (id: string) => {
    return notices.find(notice => notice.id === id);
  };

  return (
    <NoticeContext.Provider value={{ notices, addNotice, updateNotice, deleteNotice, getNotice }}>
      {children}
    </NoticeContext.Provider>
  );
};

export const useNotices = () => {
  const context = useContext(NoticeContext);
  if (!context) {
    throw new Error('useNotices must be used within a NoticeProvider');
  }
  return context;
};
